import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaPlus, FaSearch, FaShieldAlt, FaTimes } from 'react-icons/fa';
import { facilityService, settingsService, vendorService } from '../../services/dataService';
import { useAuth } from '../../hooks/useAuth';
import { hasPermission } from '../../utils/permissions';
import styles from './SettingsUsersPage.module.css';

const initialForm = {
  username: '',
  email: '',
  full_name: '',
  password: '',
  is_active: true,
  is_superuser: false,
  is_facility: false,
  is_vendor: false,
  facility_id: '',
  vendor_id: '',
};

const normalizeApiError = (error, fallback) => (
  error?.response?.data?.detail || error?.detail || fallback
);

const permissionLabel = (permission) => (
  permission.name || permission.code || `${permission.resource}:${permission.action}`
);

const formatDateTime = (value) => (
  value ? new Date(value).toLocaleString() : '-'
);

const SettingsUsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);

  const pageSize = 20;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canWriteUsers = hasPermission(currentUser, 'users', 'write');

  const permissionGroups = useMemo(() => (
    permissions.reduce((groups, permission) => {
      const key = permission.resource || 'general';
      if (!groups[key]) groups[key] = [];
      groups[key].push(permission);
      return groups;
    }, {})
  ), [permissions]);

  const facilityById = useMemo(() => (
    facilities.reduce((acc, facility) => {
      acc[facility.id] = facility;
      return acc;
    }, {})
  ), [facilities]);

  const vendorById = useMemo(() => (
    vendors.reduce((acc, vendor) => {
      acc[vendor.id] = vendor;
      return acc;
    }, {})
  ), [vendors]);

  const userLinkLabel = (user) => {
    const labels = [];
    if (user.is_facility) {
      labels.push(`Facility: ${facilityById[user.facility_id]?.name || user.facility_id || '-'}`);
    }
    if (user.is_vendor) {
      labels.push(`Vendor: ${vendorById[user.vendor_id]?.name || user.vendor_id || '-'}`);
    }
    return labels.length > 0 ? labels : ['-'];
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await settingsService.getUsers({
        page,
        page_size: pageSize,
        search: search || undefined,
        is_active: activeFilter === '' ? undefined : activeFilter === 'active',
      });
      setUsers(response.items || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, page, search]);

  const loadPermissions = useCallback(async () => {
    try {
      const response = await settingsService.getPermissions({ page_size: 100, is_active: true });
      setPermissions(response.items || []);
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to load permissions'));
    }
  }, []);

  const loadAccountLinks = useCallback(async () => {
    try {
      const [facilityItems, vendorItems] = await Promise.all([
        facilityService.getAllPages(),
        vendorService.getAllPages(),
      ]);
      setFacilities(facilityItems || []);
      setVendors(vendorItems || []);
    } catch (_) {
      setFacilities([]);
      setVendors([]);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  useEffect(() => {
    loadAccountLinks();
  }, [loadAccountLinks]);

  const resetForm = () => {
    setShowForm(false);
    setEditUser(null);
    setForm(initialForm);
    setSelectedPermissionIds([]);
  };

  const startCreate = () => {
    setError('');
    setEditUser(null);
    setForm(initialForm);
    setSelectedPermissionIds([]);
    setShowForm(true);
  };

  const startEdit = (user) => {
    setError('');
    setEditUser(user);
    setForm({
      username: user.username || '',
      email: user.email || '',
      full_name: user.full_name || '',
      password: '',
      is_active: Boolean(user.is_active),
      is_superuser: Boolean(user.is_superuser),
      is_facility: Boolean(user.is_facility),
      is_vendor: Boolean(user.is_vendor),
      facility_id: user.facility_id || '',
      vendor_id: user.vendor_id || '',
    });
    setSelectedPermissionIds((user.permissions || []).map((permission) => permission.id));
    setShowForm(true);
  };

  const validateForm = () => {
    if (!editUser && form.username.trim().length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (!form.email.trim()) {
      return 'Email is required';
    }
    if (!editUser && form.password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (form.password && form.password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (form.is_facility && !form.facility_id) {
      return 'Select a facility for facility users';
    }
    if (form.is_vendor && !form.vendor_id) {
      return 'Select a vendor for vendor users';
    }
    return '';
  };

  const syncPermissions = async (userId, previousPermissions) => {
    const previousIds = new Set((previousPermissions || []).map((permission) => permission.id));
    const nextIds = new Set(selectedPermissionIds);
    const toAssign = [...nextIds].filter((id) => !previousIds.has(id));
    const toRemove = [...previousIds].filter((id) => !nextIds.has(id));

    await Promise.all([
      ...toAssign.map((permissionId) => settingsService.assignUserPermission(userId, permissionId)),
      ...toRemove.map((permissionId) => settingsService.unassignUserPermission(userId, permissionId)),
    ]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    try {
      let savedUser;
      if (editUser) {
        const payload = {
          email: form.email.trim(),
          full_name: form.full_name.trim() || null,
          is_active: form.is_active,
          is_superuser: form.is_superuser,
          is_facility: form.is_facility,
          is_vendor: form.is_vendor,
          facility_id: form.is_facility ? form.facility_id : null,
          vendor_id: form.is_vendor ? form.vendor_id : null,
        };
        if (form.password) payload.password = form.password;
        savedUser = await settingsService.updateUser(editUser.id, payload);
        await syncPermissions(editUser.id, editUser.permissions || []);
      } else {
        savedUser = await settingsService.createUser({
          username: form.username.trim(),
          email: form.email.trim(),
          full_name: form.full_name.trim() || null,
          password: form.password,
          is_active: form.is_active,
          is_superuser: form.is_superuser,
          is_facility: form.is_facility,
          is_vendor: form.is_vendor,
          facility_id: form.is_facility ? form.facility_id : null,
          vendor_id: form.is_vendor ? form.vendor_id : null,
        });
        await syncPermissions(savedUser.id, []);
      }
      resetForm();
      await loadUsers();
    } catch (err) {
      setError(normalizeApiError(err, 'Failed to save user'));
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissionIds((current) => (
      current.includes(permissionId)
        ? current.filter((id) => id !== permissionId)
        : [...current, permissionId]
    ));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Users & Permissions</h1>
          <p>{total} user{total === 1 ? '' : 's'}</p>
        </div>
        <div className={styles.headerActions}>
          <Link className={styles.backLink} to="/settings">Back to Settings</Link>
          <button type="button" className={styles.addButton} onClick={startCreate} disabled={!canWriteUsers}>
            <FaPlus aria-hidden="true" />
            <span>Add User</span>
          </button>
        </div>
      </header>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.panel}>
        <form className={styles.filterRow} onSubmit={applyFilters}>
          <div className={styles.searchField}>
            <FaSearch aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search users"
            />
          </div>
          <select value={activeFilter} onChange={(event) => { setActiveFilter(event.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button type="submit" disabled={loading}>
            <FaSearch aria-hidden="true" />
            <span>Search</span>
          </button>
        </form>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Role</th>
                <th>Account Link</th>
                <th>Permissions</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className={styles.userCell}>
                      <strong>{user.username}</strong>
                      <span>{user.full_name || user.email}</span>
                      {user.full_name ? <small>{user.email}</small> : null}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${user.is_active ? styles.active : styles.inactive}`}>
                      {user.is_active ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td>{user.is_superuser ? 'Superuser' : 'User'}</td>
                  <td>
                    <div className={styles.linkCell}>
                      {userLinkLabel(user).map((label) => (
                        <span key={label}>{label}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className={styles.permissionChips}>
                      {(user.permissions || []).slice(0, 3).map((permission) => (
                        <span key={permission.id}>{permission.code}</span>
                      ))}
                      {(user.permissions || []).length > 3 ? (
                        <span>+{user.permissions.length - 3}</span>
                      ) : null}
                      {(user.permissions || []).length === 0 ? '-' : null}
                    </div>
                  </td>
                  <td>{formatDateTime(user.last_login_at)}</td>
                  <td>
                    <button type="button" className={styles.iconButton} onClick={() => startEdit(user)} title="Edit user" disabled={!canWriteUsers}>
                      <FaEdit aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.emptyState}>No users found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>
            Previous
          </button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" disabled={page >= pageCount || loading} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}>
            Next
          </button>
        </div>
      </section>

      {showForm ? (
        <div className={styles.modalOverlay}>
          <form className={styles.modal} onSubmit={handleSubmit}>
            <div className={styles.modalHeader}>
              <div>
                <h2>{editUser ? 'Edit User' : 'Add User'}</h2>
                <p>{selectedPermissionIds.length} permission{selectedPermissionIds.length === 1 ? '' : 's'} selected</p>
              </div>
              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving || !canWriteUsers}>{saving ? 'Saving...' : 'Save'}</button>
                <button type="button" className={styles.secondaryButton} onClick={resetForm} disabled={saving}>
                  <FaTimes aria-hidden="true" />
                  <span>Cancel</span>
                </button>
              </div>
            </div>

            <div className={styles.formGrid}>
              <label>
                <span>Username</span>
                <input
                  value={form.username}
                  disabled={Boolean(editUser)}
                  onChange={(event) => setForm((value) => ({ ...value, username: event.target.value }))}
                  required={!editUser}
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm((value) => ({ ...value, email: event.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Full Name</span>
                <input
                  value={form.full_name}
                  onChange={(event) => setForm((value) => ({ ...value, full_name: event.target.value }))}
                />
              </label>
              <label>
                <span>Password</span>
                <input
                  type="password"
                  value={form.password}
                  onChange={(event) => setForm((value) => ({ ...value, password: event.target.value }))}
                  placeholder={editUser ? 'Leave blank to keep current password' : ''}
                  required={!editUser}
                />
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) => setForm((value) => ({ ...value, is_active: event.target.checked }))}
                />
                <span>Active</span>
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_superuser}
                  onChange={(event) => setForm((value) => ({ ...value, is_superuser: event.target.checked }))}
                />
                <span>Superuser</span>
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_facility}
                  onChange={(event) => setForm((value) => ({
                    ...value,
                    is_facility: event.target.checked,
                    facility_id: event.target.checked ? value.facility_id : '',
                  }))}
                />
                <span>Facility User</span>
              </label>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_vendor}
                  onChange={(event) => setForm((value) => ({
                    ...value,
                    is_vendor: event.target.checked,
                    vendor_id: event.target.checked ? value.vendor_id : '',
                  }))}
                />
                <span>Vendor User</span>
              </label>
              <label>
                <span>Linked Facility</span>
                <select
                  value={form.facility_id}
                  onChange={(event) => setForm((value) => ({ ...value, facility_id: event.target.value }))}
                  disabled={!form.is_facility}
                  required={form.is_facility}
                >
                  <option value="">Select facility</option>
                  {facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name} ({facility.id})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Linked Vendor</span>
                <select
                  value={form.vendor_id}
                  onChange={(event) => setForm((value) => ({ ...value, vendor_id: event.target.value }))}
                  disabled={!form.is_vendor}
                  required={form.is_vendor}
                >
                  <option value="">Select vendor</option>
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <section className={styles.permissionsPanel}>
              <div className={styles.sectionTitle}>
                <FaShieldAlt aria-hidden="true" />
                <h3>Permissions</h3>
              </div>
              <div className={styles.permissionGrid}>
                {Object.entries(permissionGroups).map(([resource, group]) => (
                  <div className={styles.permissionGroup} key={resource}>
                    <h4>{resource}</h4>
                    {group.map((permission) => (
                      <label className={styles.permissionOption} key={permission.id}>
                        <input
                          type="checkbox"
                          checked={selectedPermissionIds.includes(permission.id)}
                          disabled={!canWriteUsers}
                          onChange={() => togglePermission(permission.id)}
                        />
                        <span>
                          <strong>{permissionLabel(permission)}</strong>
                          <small>{permission.code} · {permission.action}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                ))}
                {permissions.length === 0 ? (
                  <p className={styles.emptyState}>No active permissions found.</p>
                ) : null}
              </div>
            </section>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default SettingsUsersPage;
